import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { BeatLoader } from 'react-spinners'
import Sidebar from '../../layouts/Sidebar'
import Topbar from '../../layouts/Topbar'
import api from '../../config/api'

/* ─── helpers ─────────────────────────────────────────────────────────────── */

const getErrorMessage = (err, fallback) => {
    const data = err.response?.data
    if (!data) return fallback
    if (typeof data === 'string') return data
    if (data.detail) return data.detail
    if (data.message) return data.message
    return Object.entries(data)
        .map(([field, messages]) =>
            `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`
        )
        .join(' | ')
}

const addDays = (days) => {
    const date = new Date()
    date.setDate(date.getDate() + (parseInt(days, 10) || 0))
    return date.toISOString().split('T')[0]
}

const emptyLineItem = (sortOrder = 0) => ({
    product: '',
    name: '',
    description: '',
    quantity: '1.00',
    unit_price: '0.00',
    sort_order: sortOrder,
})

const emptyForm = {
    title: '',
    client: '',
    company_name: '',
    company_logo: '',
    currency: 'USD',
    valid_days: 30,
    expiry_date: addDays(30),
    payment_instructions: '',
    notes: '',
}

const emptyWalkIn = {
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
}

/* ─── component ───────────────────────────────────────────────────────────── */

const CreateQuotation = () => {
    const navigate = useNavigate()

    const [customerMode, setCustomerMode] = useState('existing') // 'existing' | 'walkin'

    const [formData, setFormData] = useState(emptyForm)
    const [walkInData, setWalkInData] = useState(emptyWalkIn)
    const [logoPreview, setLogoPreview] = useState(null)
    const [lineItems, setLineItems] = useState([emptyLineItem(0)])

    const [clients, setClients] = useState([])
    const [products, setProducts] = useState([])
    const [clientsLoading, setClientsLoading] = useState(true)
    const [productsLoading, setProductsLoading] = useState(true)

    const [submitting, setSubmitting] = useState(false)
    const [formError, setFormError] = useState(null)
    const [successMessage, setSuccessMessage] = useState(null)

    /* ── fetch dropdown data ── */
    const fetchClients = useCallback(async () => {
        setClientsLoading(true)
        try {
            const res = await api.get('/clients/')
            setClients(Array.isArray(res.data) ? res.data : (res.data?.results ?? []))
        } catch (err) {
            console.error('Failed to fetch clients', err)
        } finally {
            setClientsLoading(false)
        }
    }, [])

    const fetchProducts = useCallback(async () => {
        setProductsLoading(true)
        try {
            const res = await api.get('/products/')
            setProducts(Array.isArray(res.data) ? res.data : (res.data?.results ?? []))
        } catch (err) {
            console.error('Failed to fetch products', err)
        } finally {
            setProductsLoading(false)
        }
    }, [])

    useEffect(() => {
        fetchClients()
        fetchProducts()
    }, [fetchClients, fetchProducts])

    /* ── form handlers ── */
    const handleChange = (e) => {
        const { name, value } = e.target
        if (name === 'valid_days') {
            setFormData((prev) => ({
                ...prev,
                valid_days: value,
                expiry_date: value !== '' ? addDays(value) : '',
            }))
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }))
        }
    }

    const handleWalkInChange = (e) => {
        const { name, value } = e.target
        setWalkInData((prev) => ({ ...prev, [name]: value }))
    }

    const handleModeChange = (mode) => {
        setCustomerMode(mode)
        setFormError(null)
        // Reset client / walk-in selections when switching
        setFormData((prev) => ({ ...prev, client: '' }))
        setWalkInData(emptyWalkIn)
    }

    /* ── logo upload ── */
    const handleLogoChange = (e) => {
        const file = e.target.files?.[0]
        if (!file) return
        const reader = new FileReader()
        reader.onload = (ev) => {
            const dataUrl = ev.target.result
            setLogoPreview(dataUrl)
            setFormData((prev) => ({ ...prev, company_logo: dataUrl }))
        }
        reader.readAsDataURL(file)
    }

    /* ── line-item handlers ── */
    const handleLineItemChange = (index, e) => {
        const { name, value } = e.target
        setLineItems((prev) => {
            const updated = [...prev]
            updated[index] = { ...updated[index], [name]: value }
            return updated
        })
    }

    const handleLineItemProductChange = (index, productId) => {
        const product = products.find((p) => String(p.id) === String(productId))
        setLineItems((prev) => {
            const updated = [...prev]
            updated[index] = {
                ...updated[index],
                product: productId,
                name: product?.name ?? updated[index].name,
                description: product?.description ?? updated[index].description,
                unit_price: product?.price ?? product?.unit_price ?? updated[index].unit_price,
            }
            return updated
        })
    }

    const addLineItem = () => setLineItems((prev) => [...prev, emptyLineItem(prev.length)])

    const removeLineItem = (index) =>
        setLineItems((prev) =>
            prev
                .filter((_, i) => i !== index)
                .map((item, i) => ({ ...item, sort_order: i }))
        )

    /* ── helpers ── */
    const getClientLabel = (client) => {
        const name = [client.first_name, client.last_name].filter(Boolean).join(' ')
        return name || client.company?.name || client.name || `Client #${client.id}`
    }

    const lineItemTotal = (item) => {
        const qty = parseFloat(item.quantity) || 0
        const price = parseFloat(item.unit_price) || 0
        return (qty * price).toFixed(2)
    }

    const grandTotal = lineItems.reduce(
        (sum, item) => sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0),
        0
    )

    /* ── submission ── */
    const handleSubmit = async (e) => {
        e.preventDefault()
        setSubmitting(true)
        setFormError(null)
        setSuccessMessage(null)

        let clientId = formData.client

        // ── Walk-in: create client first ──
        if (customerMode === 'walkin') {
            if (!walkInData.first_name.trim()) {
                setFormError('Please enter the walk-in customer\'s first name.')
                setSubmitting(false)
                return
            }
            try {
                const clientRes = await api.post('/clients/', {
                    first_name: walkInData.first_name.trim(),
                    last_name: walkInData.last_name.trim(),
                    email: walkInData.email.trim(),
                    phone: walkInData.phone.trim(),
                    notes: 'Walk-in customer — created from quotation.',
                    is_walk_in: true,
                })
                clientId = clientRes.data.id
            } catch (err) {
                console.error('Failed to create walk-in client', err)
                setFormError(
                    'Could not save walk-in customer: ' +
                    getErrorMessage(err, 'Please check the details and try again.')
                )
                setSubmitting(false)
                return
            }
        } else {
            if (!clientId) {
                setFormError('Please select a client.')
                setSubmitting(false)
                return
            }
        }

        const payload = {
            client: Number(clientId),
            title: formData.title,
            company_name: formData.company_name,
            company_logo: formData.company_logo,
            currency: formData.currency,
            expiry_date: formData.expiry_date,
            payment_instructions: formData.payment_instructions,
            notes: formData.notes,
            line_items: lineItems.map((item, i) => ({
                ...(item.product ? { product: Number(item.product) } : {}),
                name: item.name,
                description: item.description,
                quantity: item.quantity,
                unit_price: item.unit_price,
                sort_order: i,
            })),
        }

        try {
            await api.post('/quotations/', payload)
            setSuccessMessage('Quotation created successfully.')
            setTimeout(() => navigate('/quotations'), 1200)
        } catch (err) {
            console.error('Failed to create quotation', err)
            setFormError(getErrorMessage(err, 'Failed to create quotation. Please try again.'))
        } finally {
            setSubmitting(false)
        }
    }

    /* ── render ── */
    return (
        <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
            <Topbar />
            <Sidebar />

            <div className="page-wrapper">
                <div className="content pb-0">

                    {/* Page header */}
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
                        <div>
                            <h4 className="fw-bold mb-1">Create Quotation</h4>
                        </div>
                        <Link to="/quotations" className="btn btn-light">
                            <i className="ti ti-arrow-left me-1" />
                            Back to Quotations
                        </Link>
                    </div>

                    <form onSubmit={handleSubmit}>
                        {/* Alerts */}
                        {formError && (
                            <div className="alert alert-danger mb-4" role="alert">
                                <i className="ti ti-alert-circle me-2" />
                                {formError}
                            </div>
                        )}
                        {successMessage && (
                            <div className="alert alert-success mb-4" role="alert">
                                <i className="ti ti-check me-2" />
                                {successMessage}
                            </div>
                        )}

                        {/* ── Quotation Details ── */}
                        <div className="card shadow-sm mb-4">
                            <div className="card-header">
                                <h5 className="fw-bold mb-0">Quotation Details</h5>
                            </div>
                            <div className="card-body">
                                <div className="row g-3">

                                    {/* Title */}
                                    <div className="col-md-6">
                                        <label htmlFor="quote-title" className="form-label">
                                            Quotation Title <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            id="quote-title"
                                            type="text"
                                            name="title"
                                            className="form-control"
                                            placeholder="e.g. Website Redesign Proposal"
                                            value={formData.title}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>

                                    {/* Customer Mode Toggle */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Customer Type <span className="text-danger">*</span>
                                        </label>
                                        <div className="d-flex gap-2">
                                            <button
                                                type="button"
                                                className={`btn btn-sm flex-fill ${customerMode === 'existing' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                onClick={() => handleModeChange('existing')}
                                            >
                                                <i className="ti ti-users me-1" />
                                                Existing Client
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn btn-sm flex-fill ${customerMode === 'walkin' ? 'btn-warning text-dark' : 'btn-outline-secondary'}`}
                                                onClick={() => handleModeChange('walkin')}
                                            >
                                                <i className="ti ti-walk me-1" />
                                                Walk-in Customer
                                            </button>
                                        </div>
                                    </div>

                                    {/* ── Existing Client dropdown ── */}
                                    {customerMode === 'existing' && (
                                        <div className="col-md-6">
                                            <label htmlFor="quote-client" className="form-label">
                                                Client <span className="text-danger">*</span>
                                            </label>
                                            <select
                                                id="quote-client"
                                                name="client"
                                                className="form-select"
                                                value={formData.client}
                                                onChange={handleChange}
                                                required={customerMode === 'existing'}
                                                disabled={clientsLoading}
                                            >
                                                <option value="">
                                                    {clientsLoading ? 'Loading clients…' : '— Select a client —'}
                                                </option>
                                                {clients.map((c) => (
                                                    <option key={c.id} value={c.id}>
                                                        {getClientLabel(c)}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    {/* ── Walk-in Customer fields ── */}
                                    {customerMode === 'walkin' && (
                                        <>
                                            <div className="col-12">
                                                <div className="alert alert-warning py-2 mb-0 d-flex align-items-center gap-2">
                                                    <i className="ti ti-info-circle fs-5" />
                                                    <span>
                                                        A new client record will be created automatically for this walk-in customer.
                                                        If the quotation is accepted, they can be converted to a full client.
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="col-md-3">
                                                <label htmlFor="walkin-first-name" className="form-label">
                                                    First Name <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                    id="walkin-first-name"
                                                    type="text"
                                                    name="first_name"
                                                    className="form-control"
                                                    placeholder="e.g. John"
                                                    value={walkInData.first_name}
                                                    onChange={handleWalkInChange}
                                                    required={customerMode === 'walkin'}
                                                />
                                            </div>
                                            <div className="col-md-3">
                                                <label htmlFor="walkin-last-name" className="form-label">
                                                    Last Name
                                                </label>
                                                <input
                                                    id="walkin-last-name"
                                                    type="text"
                                                    name="last_name"
                                                    className="form-control"
                                                    placeholder="e.g. Doe"
                                                    value={walkInData.last_name}
                                                    onChange={handleWalkInChange}
                                                />
                                            </div>
                                            <div className="col-md-3">
                                                <label htmlFor="walkin-email" className="form-label">
                                                    Email
                                                </label>
                                                <input
                                                    id="walkin-email"
                                                    type="email"
                                                    name="email"
                                                    className="form-control"
                                                    placeholder="e.g. john@example.com"
                                                    value={walkInData.email}
                                                    onChange={handleWalkInChange}
                                                />
                                            </div>
                                            <div className="col-md-3">
                                                <label htmlFor="walkin-phone" className="form-label">
                                                    Phone
                                                </label>
                                                <input
                                                    id="walkin-phone"
                                                    type="tel"
                                                    name="phone"
                                                    className="form-control"
                                                    placeholder="e.g. +1 555 0000"
                                                    value={walkInData.phone}
                                                    onChange={handleWalkInChange}
                                                />
                                            </div>
                                        </>
                                    )}

                                    {/* Company Name */}
                                    <div className="col-md-6">
                                        <label htmlFor="quote-company-name" className="form-label">
                                            Company Name
                                        </label>
                                        <input
                                            id="quote-company-name"
                                            type="text"
                                            name="company_name"
                                            className="form-control"
                                            placeholder="e.g. Acme Corporation"
                                            value={formData.company_name}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    {/* Company Logo */}
                                    <div className="col-md-6">
                                        <label htmlFor="quote-company-logo" className="form-label">
                                            Company Logo
                                        </label>
                                        <div className="d-flex align-items-center gap-3 flex-wrap">
                                            {logoPreview && (
                                                <img
                                                    src={logoPreview}
                                                    alt="Company logo preview"
                                                    style={{
                                                        height: 48,
                                                        maxWidth: 140,
                                                        objectFit: 'contain',
                                                        border: '1px solid #dee2e6',
                                                        borderRadius: 6,
                                                        padding: 3,
                                                        background: '#fff',
                                                    }}
                                                />
                                            )}
                                            <div className="flex-fill">
                                                <input
                                                    id="quote-company-logo"
                                                    type="file"
                                                    accept="image/*"
                                                    className="form-control"
                                                    onChange={handleLogoChange}
                                                />
                                                <div className="form-text">PNG, JPG, SVG, WEBP. Will appear on the quotation.</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Currency */}
                                    <div className="col-md-4">
                                        <label htmlFor="quote-currency" className="form-label">
                                            Currency <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            id="quote-currency"
                                            name="currency"
                                            className="form-select"
                                            value={formData.currency}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="USD">USD — US Dollar</option>
                                            <option value="ZIG">ZIG — Zimbabwe Gold</option>
                                        </select>
                                    </div>

                                    {/* Validity Days */}
                                    <div className="col-md-4">
                                        <label htmlFor="quote-valid-days" className="form-label">
                                            Validity (Days) <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            id="quote-valid-days"
                                            type="number"
                                            name="valid_days"
                                            className="form-control"
                                            min="0"
                                            placeholder="30"
                                            value={formData.valid_days}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>

                                    {/* Expiry Date */}
                                    <div className="col-md-4">
                                        <label htmlFor="quote-expiry-date" className="form-label">
                                            Expiry Date <span className="text-danger">*</span>
                                            {formData.valid_days !== '' && (
                                                <span className="text-muted fs-12 ms-1">(auto-calculated)</span>
                                            )}
                                        </label>
                                        <input
                                            id="quote-expiry-date"
                                            type="date"
                                            name="expiry_date"
                                            className="form-control"
                                            value={formData.expiry_date}
                                            onChange={handleChange}
                                            required
                                        />
                                        {formData.valid_days !== '' && (
                                            <div className="form-text">
                                                Today&nbsp;+&nbsp;{formData.valid_days}&nbsp;day{Number(formData.valid_days) !== 1 ? 's' : ''}
                                            </div>
                                        )}
                                    </div>

                                </div>
                            </div>
                        </div>

                        {/* ── Line Items ── */}
                        <div className="card shadow-sm mb-4">
                            <div className="card-header d-flex align-items-center justify-content-between">
                                <h5 className="fw-bold mb-0">Products / Services</h5>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-primary"
                                    onClick={addLineItem}
                                    disabled={productsLoading}
                                >
                                    <i className="ti ti-plus me-1" />
                                    Add Line Item
                                </button>
                            </div>
                            <div className="card-body p-0">
                                <div className="table-responsive">
                                    <table className="table table-nowrap border mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th style={{ minWidth: 180 }}>Product / Service</th>
                                                <th style={{ minWidth: 160 }}>Name</th>
                                                <th style={{ minWidth: 200 }}>Description</th>
                                                <th style={{ minWidth: 100 }}>Qty</th>
                                                <th style={{ minWidth: 120 }}>Unit Price</th>
                                                <th style={{ minWidth: 110 }}>Total</th>
                                                <th style={{ width: 50 }} />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {lineItems.map((item, index) => (
                                                <tr key={index}>
                                                    {/* Product select */}
                                                    <td>
                                                        <select
                                                            className="form-select form-select-sm"
                                                            value={item.product}
                                                            onChange={(e) =>
                                                                handleLineItemProductChange(index, e.target.value)
                                                            }
                                                            disabled={productsLoading}
                                                            aria-label={`Product for line item ${index + 1}`}
                                                        >
                                                            <option value="">
                                                                {productsLoading ? 'Loading…' : '— Select —'}
                                                            </option>
                                                            {products.map((p) => (
                                                                <option key={p.id} value={p.id}>
                                                                    {p.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </td>

                                                    {/* Name */}
                                                    <td>
                                                        <input
                                                            type="text"
                                                            name="name"
                                                            className="form-control form-control-sm"
                                                            placeholder="Item name"
                                                            value={item.name}
                                                            onChange={(e) => handleLineItemChange(index, e)}
                                                            required
                                                            aria-label={`Name for line item ${index + 1}`}
                                                        />
                                                    </td>

                                                    {/* Description */}
                                                    <td>
                                                        <input
                                                            type="text"
                                                            name="description"
                                                            className="form-control form-control-sm"
                                                            placeholder="Short description"
                                                            value={item.description}
                                                            onChange={(e) => handleLineItemChange(index, e)}
                                                            aria-label={`Description for line item ${index + 1}`}
                                                        />
                                                    </td>

                                                    {/* Quantity */}
                                                    <td>
                                                        <input
                                                            type="number"
                                                            name="quantity"
                                                            className="form-control form-control-sm"
                                                            min="0.01"
                                                            step="0.01"
                                                            value={item.quantity}
                                                            onChange={(e) => handleLineItemChange(index, e)}
                                                            required
                                                            aria-label={`Quantity for line item ${index + 1}`}
                                                        />
                                                    </td>

                                                    {/* Unit Price */}
                                                    <td>
                                                        <input
                                                            type="number"
                                                            name="unit_price"
                                                            className="form-control form-control-sm"
                                                            min="0"
                                                            step="0.01"
                                                            value={item.unit_price}
                                                            onChange={(e) => handleLineItemChange(index, e)}
                                                            required
                                                            aria-label={`Unit price for line item ${index + 1}`}
                                                        />
                                                    </td>

                                                    {/* Row total */}
                                                    <td className="fw-medium text-end">
                                                        {formData.currency === 'USD' ? '$' : 'ZIG '}
                                                        {lineItemTotal(item)}
                                                    </td>

                                                    {/* Remove */}
                                                    <td className="text-center">
                                                        {lineItems.length > 1 && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-danger p-1"
                                                                onClick={() => removeLineItem(index)}
                                                                title="Remove line item"
                                                                aria-label={`Remove line item ${index + 1}`}
                                                            >
                                                                <i className="ti ti-trash" />
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot className="table-light">
                                            <tr>
                                                <td colSpan={5} className="text-end fw-bold">Grand Total</td>
                                                <td className="fw-bold text-end">
                                                    {formData.currency === 'USD' ? '$' : 'ZIG '}
                                                    {grandTotal.toFixed(2)}
                                                </td>
                                                <td />
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                        </div>

                        {/* ── Additional Info ── */}
                        <div className="card shadow-sm mb-4">
                            <div className="card-header">
                                <h5 className="fw-bold mb-0">Additional Information</h5>
                            </div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label htmlFor="quote-terms" className="form-label">Terms / Payment Instructions</label>
                                        <textarea
                                            id="quote-terms"
                                            name="payment_instructions"
                                            className="form-control"
                                            rows={4}
                                            placeholder="e.g. 50% deposit required to proceed."
                                            value={formData.payment_instructions}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label htmlFor="quote-notes" className="form-label">Notes</label>
                                        <textarea
                                            id="quote-notes"
                                            name="notes"
                                            className="form-control"
                                            rows={4}
                                            placeholder="e.g. Scope includes design and two revision rounds"
                                            value={formData.notes}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ── Actions ── */}
                        <div className="d-flex align-items-center justify-content-end gap-2 mb-4">
                            <Link
                                to="/quotations"
                                className="btn btn-light"
                                onClick={(e) => submitting && e.preventDefault()}
                            >
                                Cancel
                            </Link>
                            <button type="submit" className="btn btn-primary" disabled={submitting}>
                                {submitting ? <BeatLoader color="#fff" size={8} /> : 'Create Quotation'}
                            </button>
                        </div>
                    </form>
                </div>

                <div className="footer d-flex align-items-center justify-content-between flex-column flex-sm-row row-gap-2 border-top py-2 px-3">
                    <p className="text-dark mb-0">
                        {new Date().getFullYear()} © <span className="link-primary">Burray Invoice</span>, All Rights Reserved
                    </p>
                </div>
            </div>
        </div>
    )
}

export default CreateQuotation
